import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { track } from '@amplitude/analytics-browser';
import { GreenroomState as GS } from '../stores/types/types';
import * as SettingsActions from '../stores/actions/settings';
import * as ShotsActions from '../stores/actions/shots';
import { ROOM_STATES } from '../stores/reducers/settings';
import Socket from '../connection/socket';
import useShots from './use-shots';
import usePrevious from '../utils/use-previous';
import { DeleteAllTimersForRoom } from '../connection/autoplay';
import { AmpEvent } from '../types/amplitude';
import { RoomStates } from '../types/enums';
import { UpdateRoomStateEntity } from '../connection/meeting';

const useRoomState = (socket: Socket | null) => {
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const clearShot = useSelector((s: GS) => s.SettingsReducer.clearShot);
	const roomStateEntity = useSelector((s: GS) => s.MeetingReducer.roomStateEntity);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const shotStartTimestampEntity = useSelector((s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const session = useSelector((s: GS) => s.SessionReducer.session);

	const dispatch = useDispatch();
	const { handleSelectShot } = useShots();
	const prevRoomState = usePrevious(roomState);

	useEffect(() => {
		if (roomState === ROOM_STATES.SHOWTIME && prevRoomState) {
			dispatch(ShotsActions.setShotTabShotId(shots[0].shot_id));
		} else if (prevRoomState) {
			handleSelectShot(shots[0].shot_id);
			dispatch(ShotsActions.setShotTabShotId(shots[0].shot_id));
		}
	}, [roomState, dispatch]);

	async function swapRoomState(newState: RoomStates) {
		if (!token || !meeting || !user || !roomStateEntity) {
			return;
		}

		track(newState === RoomStates.live ? AmpEvent.exitedShowbuilder : AmpEvent.enteredShowbuilder);

		// update room state entity on beehive
		const newEntity = { ...roomStateEntity };
		newEntity.entityValue.roomState = newState;
		const updated = await UpdateRoomStateEntity(token, meeting?.meeting, user?.gr_user, newEntity);
		if (updated && newState === RoomStates.live) {
			if (clearShot) {
				dispatch(SettingsActions.setClearShot(false));
			}

			handleSelectShot(shots[0].shot_id, false);
		}
	}

	function cycleRoomState() {
		switch (roomState) {
			case ROOM_STATES.SHOWTIME:
				swapRoomState(RoomStates.showbuilder);
				if (token && meeting?.meeting) {
					DeleteAllTimersForRoom(token, meeting.meeting);
				}
				break;
			case ROOM_STATES.EDITING:
				swapRoomState(RoomStates.live);
				break;
			default:
				break;
		}
	}

	return { cycleRoomState };
};

export default useRoomState;
